import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  DollarSign,
  TrendingUp,
  Building2,
  Receipt,
  ArrowUpRight,
  Zap,
  ExternalLink,
  ArrowRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CommissionPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Redirect non-admin users to dashboard
    if (user && user.role !== "admin") {
      navigate("/dashboard", { replace: true });
      return;
    }

    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/admin/commissions`, { withCredentials: true });
        setData(res.data);
      } catch (err) {
        console.error("Failed to load commission data:", err);
        // If 403, redirect to dashboard
        if (err.response?.status === 403) {
          navigate("/dashboard", { replace: true });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, navigate]);

  const formatCurrency = (amount, currency = "EUR") => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currency?.toUpperCase() || "EUR",
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="commission-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("commission.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("commission.subtitle")}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("commission.totalCommission")}</p>
                <p className="text-2xl sm:text-3xl font-bold mt-2 tracking-tight text-emerald-500">
                  {formatCurrency(data?.total_commission)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <DollarSign className="h-6 w-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("commission.monthlyCommission")}</p>
                <p className="text-2xl sm:text-3xl font-bold mt-2 tracking-tight">
                  {formatCurrency(data?.monthly_commission)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-blue-500/10">
                <TrendingUp className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("commission.totalVolume")}</p>
                <p className="text-2xl sm:text-3xl font-bold mt-2 tracking-tight">
                  {formatCurrency(data?.total_volume)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-purple-500/10">
                <Receipt className="h-6 w-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("commission.transactions")}</p>
                <p className="text-2xl sm:text-3xl font-bold mt-2 tracking-tight">
                  {data?.total_transactions || 0}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-amber-500/10">
                <ArrowUpRight className="h-6 w-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payout Account */}
      <Card className="border-border" data-testid="payout-info">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Building2 className="h-5 w-5 text-blue-500" />
            </div>
            {t("commission.payoutAccount")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/50 rounded-xl p-4 sm:p-6 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
              <span className="text-sm text-muted-foreground">{t("commission.accountHolder")}</span>
              <span className="font-medium">{data?.payout_info?.holder}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
              <span className="text-sm text-muted-foreground">IBAN</span>
              <span className="font-mono font-medium text-sm">{data?.payout_info?.iban}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
              <span className="text-sm text-muted-foreground">BIC / SWIFT</span>
              <span className="font-mono font-medium">{data?.payout_info?.bic}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stripe Auto Payout + Flux Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border" data-testid="stripe-payout-info">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="p-2 rounded-lg bg-violet-500/10">
                <Zap className="h-5 w-5 text-violet-500" />
              </div>
              {t("commission.stripePayoutTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("commission.stripePayoutDesc")}
            </p>
            <p className="text-xs text-muted-foreground/80">
              {t("commission.stripePayoutSchedule")}
            </p>
            <a
              href="https://dashboard.stripe.com/settings/payouts"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
              data-testid="stripe-dashboard-link"
            >
              {t("commission.stripePayoutAction")}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </CardContent>
        </Card>

        <Card className="border-border" data-testid="flux-breakdown">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <ArrowRight className="h-5 w-5 text-amber-500" />
              </div>
              Flux
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-sm font-bold text-blue-500">1</span>
              </div>
              <div>
                <p className="font-medium text-sm">{t("commission.flux1Label")}</p>
                <p className="text-xs text-muted-foreground">{t("commission.flux1Desc")}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-sm font-bold text-emerald-500">2</span>
              </div>
              <div>
                <p className="font-medium text-sm">{t("commission.flux2Label")}</p>
                <p className="text-xs text-muted-foreground">{t("commission.flux2Desc")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Commissions */}
      <Card className="border-border" data-testid="recent-commissions">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <Receipt className="h-5 w-5 text-emerald-500" />
            </div>
            {t("commission.recentCommissions")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data?.recent_commissions?.length > 0 ? (
            <div className="space-y-3">
              {data.recent_commissions.map((entry) => (
                <div
                  key={entry.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-xl bg-muted/50 gap-2 sm:gap-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <DollarSign className="h-5 w-5 text-emerald-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{entry.product}</p>
                      <p className="text-xs text-muted-foreground">
                        {entry.creator_name} &bull; {new Date(entry.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4 text-sm ml-13 sm:ml-0">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">{t("commission.buyerPaid")}</p>
                      <p className="font-medium">{formatCurrency(entry.buyer_paid, entry.currency)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">{t("commission.creatorGets")}</p>
                      <p className="font-medium">{formatCurrency(entry.creator_receives, entry.currency)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">{t("commission.yourCommission")}</p>
                      <p className="font-bold text-emerald-500">{formatCurrency(entry.commission, entry.currency)}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${entry.status === "paid" ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-500/10 text-amber-500"}`}>
                        {entry.status === "paid" ? "Paid" : "Pending"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="font-medium text-muted-foreground">{t("commission.noCommissions")}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("commission.noCommissionsDesc")}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
